import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import { fetchAlpacaPortfolioHistory } from '../../service/AlpacaService';

const EquityCurveChart = () => {
  const [portfolioData, setPortfolioData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState('1M'); // 1W, 1M, 3M, 6M, 1Y, ALL

  useEffect(() => {
    loadPortfolioHistory();
  }, [timePeriod]); // Re-fetch when time period changes

  const loadPortfolioHistory = async () => {
    try {
      setLoading(true);
      
      // Map time periods to Alpaca API timeframe and period parameters
      const timeframeMap = {
        '1W': { timeframe: '1H', period: '1W' },
        '1M': { timeframe: '1D', period: '1M' },
        '3M': { timeframe: '1D', period: '3M' },
        '6M': { timeframe: '1D', period: '6M' },
        '1Y': { timeframe: '1W', period: '1A' },
        'ALL': { timeframe: '1W', period: 'all' }
      };
      
      const params = timeframeMap[timePeriod] || timeframeMap['1M'];
      const data = await fetchAlpacaPortfolioHistory(params.period, params.timeframe);
      
      // Process portfolio history data
      // Alpaca returns: { timestamp: [], equity: [], profit_loss: [], ... }
      if (data && data.timestamp && data.equity) {
        const chartData = data.timestamp.map((ts, index) => ({
          timestamp: ts * 1000, // Convert to milliseconds for ApexCharts
          date: new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          equity: parseFloat(data.equity[index]),
          pl: parseFloat(data.profit_loss?.[index] || 0)
        }));
        
        setPortfolioData(chartData);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading portfolio history:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '30px',
        marginTop: '20px',
        textAlign: 'center'
      }}>
        <div className="spinner-border spinner-border-sm text-success mb-2" role="status"></div>
        <div style={{ color: '#888', fontSize: '14px' }}>Loading equity curve...</div>
      </div>
    );
  }

  if (!portfolioData || portfolioData.length === 0) {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '30px',
        marginTop: '20px',
        textAlign: 'center'
      }}>
        <div style={{ color: '#888', fontSize: '14px' }}>
          No portfolio history available yet
        </div>
      </div>
    );
  }

  // Calculate stats
  const startEquity = portfolioData[0]?.equity || 0;
  const currentEquity = portfolioData[portfolioData.length - 1]?.equity || 0;
  const totalReturn = currentEquity - startEquity;
  const totalReturnPercent = startEquity > 0 ? ((currentEquity - startEquity) / startEquity) * 100 : 0;
  const isPositive = totalReturn >= 0;
  
  // Determine chart color based on performance
  const chartColor = isPositive ? '#10b981' : '#ef4444';

  // Prepare chart data for ApexCharts
  const chartSeries = [{
    name: 'Equity',
    data: portfolioData.map(point => ({
      x: point.timestamp,
      y: point.equity.toFixed(2)
    }))
  }];

  // ApexCharts options
  const chartOptions = {
    chart: {
      type: 'area',
      height: 300,
      background: 'transparent',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 2,
      colors: [chartColor]
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 90, 100],
        colorStops: [
          {
            offset: 0,
            color: chartColor,
            opacity: 0.4
          },
          {
            offset: 100,
            color: chartColor,
            opacity: 0
          }
        ]
      }
    },
    grid: {
      borderColor: '#2a2a2a',
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        style: {
          colors: '#666',
          fontSize: '12px'
        },
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM',
          day: 'dd MMM',
          hour: 'HH:mm'
        }
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#666',
          fontSize: '12px'
        },
        formatter: function(value) {
          return '$' + (value / 1000).toFixed(0) + 'k';
        }
      }
    },
    tooltip: {
      theme: 'dark',
      x: {
        format: 'dd MMM yyyy'
      },
      y: {
        formatter: function(value) {
          return '$' + parseFloat(value).toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          });
        }
      },
      style: {
        fontSize: '13px'
      }
    },
    theme: {
      mode: 'dark'
    }
  };

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      border: '1px solid #333',
      borderRadius: '8px',
      padding: '20px',
      marginTop: '20px'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h3 style={{ color: '#e0e0e0', marginBottom: '5px', fontSize: '18px' }}>
            Account Equity
          </h3>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ 
              color: '#e0e0e0', 
              fontSize: '24px', 
              fontWeight: '700' 
            }}>
              ${currentEquity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span style={{ 
              color: isPositive ? '#10b981' : '#ef4444',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              {isPositive ? '+' : ''}${totalReturn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' '}({isPositive ? '+' : ''}{totalReturnPercent.toFixed(2)}%)
            </span>
          </div>
        </div>
        
        {/* Time period selector (future enhancement) */}
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {['1W', '1M', '3M', '6M', '1Y', 'ALL'].map(period => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              style={{
                padding: '5px 10px',
                backgroundColor: timePeriod === period ? '#10b981' : 'transparent',
                border: '1px solid #333',
                borderRadius: '4px',
                color: timePeriod === period ? '#000' : '#888',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (timePeriod !== period) {
                  e.target.style.borderColor = '#10b981';
                  e.target.style.color = '#10b981';
                }
              }}
              onMouseLeave={(e) => {
                if (timePeriod !== period) {
                  e.target.style.borderColor = '#333';
                  e.target.style.color = '#888';
                }
              }}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div style={{ marginTop: '10px' }}>
        <ReactApexChart 
          options={chartOptions} 
          series={chartSeries} 
          type="area" 
          height={300} 
        />
      </div>

      {/* Stats Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        marginTop: '20px',
        paddingTop: '15px',
        borderTop: '1px solid #2a2a2a',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
            STARTING
          </div>
          <div style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: '600' }}>
            ${startEquity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
            CURRENT
          </div>
          <div style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: '600' }}>
            ${currentEquity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>
            RETURN
          </div>
          <div style={{ 
            color: isPositive ? '#10b981' : '#ef4444', 
            fontSize: '14px', 
            fontWeight: '600' 
          }}>
            {isPositive ? '+' : ''}{totalReturnPercent.toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquityCurveChart;